// --- Internal Helper Functions for Formatting ---
// We define these here to ensure they work reliably in the email environment without external dependencies.

const parseExactLocalTime = (dateString: string) => {
  if (!dateString) return new Date();
  const cleanStr = dateString.replace(/(Z|[+-]\d{2}:?\d{2})$/, "");
  return new Date(cleanStr);
};

const formatTicketDate = (dateString: string) => {
  if (!dateString) return "";
  try {
    const date = parseExactLocalTime(dateString);
    // Returns: 01 JAN 2026
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .toUpperCase();
  } catch (e) {
    return dateString;
  }
};

const formatTicketTime = (dateString: string) => {
  if (!dateString) return "";
  try {
    const date = parseExactLocalTime(dateString);
    // Returns: 14:30 (12-hour format)
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (e) {
    return "";
  }
};

export const generateTicketHtml = (booking: any, agencyInfo: any): string => {
  // ✅ NEW BULLETPROOF DURATION CALCULATOR FOR EMAIL TEMPLATES
  const calculateDuration = (
    duration: string | number | undefined,
    departure: string,
    arrival: string,
  ): string => {
    console.log("====== EMAIL TEMPLATE DURATION DEBUG ======");
    console.log("Raw Duration passed to template:", duration);

    if (duration) {
      const durStr = String(duration).trim();

      // Case 1: It's already formatted nicely (e.g. "7H 15M" or "7 hrs 15 min")
      if (
        durStr.toLowerCase().includes("h") ||
        durStr.toLowerCase().includes("m")
      ) {
        console.log("Email Outcome: Already formatted ->", durStr);
        return durStr.toUpperCase();
      }

      // Case 2: Amadeus Raw 'HHMM' format (e.g., "0715") - Exact logic from flightController
      if (durStr.length === 4 && !isNaN(Number(durStr))) {
        const hours = parseInt(durStr.slice(0, 2), 10);
        const minutes = parseInt(durStr.slice(2, 4), 10);
        console.log(`Email Outcome: Parsed HHMM -> ${hours}H ${minutes}M`);
        return `${hours}H ${minutes}M`;
      }

      // Case 3: Total Minutes fallback (e.g., "435" or 435)
      if (!isNaN(Number(durStr))) {
        const totalMinutes = parseInt(durStr, 10);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        console.log(
          `Email Outcome: Parsed Total Minutes -> ${hours}H ${minutes}M`,
        );
        return `${hours}H ${minutes}M`;
      }
    }

    console.log("Email Outcome: Fallback to Clock Math");
    if (!departure || !arrival) return "N/A";
    const depTime = new Date(departure);
    const arrTime = new Date(arrival);
    let diffMs = arrTime.getTime() - depTime.getTime();
    if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000;

    const diffMins = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;
    return `${hours}H ${minutes}M`;
  };

  const getTravelClassName = (travelClass: string): string => {
    switch (travelClass) {
      case "Y":
      case "M":
        return "Economy";
      case "W":
        return "Premium Economy";
      case "C":
        return "Business Class";
      case "F":
        return "First Class";
      default:
        return "Unknown Class";
    }
  };

  // Generate HTML for each passenger (Separate pages)
  const passengersHtml = booking.bookingDetails.passengers
    .map(
      (passenger: any) => `
    <div style="width: 100%; max-width: 800px; margin: auto; background-color: white; padding: 20px; font-family: Arial, sans-serif; color: #333;">
      
      <div style="width: 100%; max-width: 800px; margin: auto; background-color: white; padding: 32px; margin-bottom: 16px;">
      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; margin-bottom: 40px;">
        <div style="display: flex; align-items: center;">
          <img src="https://i.ibb.co/c87RxFv/findyoufare-logo.png" alt="Find Your Fares Logo" style="height: 56px;" />
        </div>
        <h4 style="text-align: center; font-size: 24px; font-weight: bold; margin: 12px 0;">e-Ticket and Itinerary</h4>
        <div style="text-align: right;">
          <h2 style="font-size: 14px; font-weight: bold; margin-top: 12px;">
            <span style="font-weight: normal;">Booking Reference:</span> ${
              booking.controlNumber
            }
          </h2>
          <h2 style="font-size: 14px; font-weight: bold; margin-top: 12px;">
            <span style="font-weight: normal;">Ticket Number:</span> ${
              passenger.ticketNumber || booking.ticketNumber || "Pending"
            }
          </h2>
        </div>
      </div>
      
      <p style="font-size: 12px; color: #555; margin-bottom: 20px; border-bottom: 1px solid #eee; padding-bottom: 15px;">
        This e-Ticket receipt/itinerary is for your record and forms part of your contract of carriage. Please ensure you complete check-in at least 90 minutes before departure.
      </p>
      
      <table width="100%" border="0" cellspacing="0" cellpadding="8" style="background-color: #cccff2; margin-bottom: 10px;">
        <tr>
          <td style="font-weight: bold; font-size: 13px; text-transform: uppercase;">Passenger and Ticket Information</td>
        </tr>
      </table>

      <table width="100%" border="0" cellspacing="0" cellpadding="6" style="margin-bottom: 25px; font-size: 12px;">
        <tr>
          <td width="25%" style="color: #666; text-transform: uppercase;">Passenger Name</td>
          <td width="25%" style="font-weight: bold; text-transform: uppercase;">${
            passenger.lastName
          }/${passenger.firstName}</td>
          <td width="25%" style="color: #666; text-transform: uppercase;">Booking Reference</td>
          <td width="25%" style="font-weight: bold; text-transform: uppercase;">${
            booking.controlNumber
          }</td>
        </tr>
        <tr>
          <td style="color: #666; text-transform: uppercase;">Passenger Type</td>
          <td style="font-weight: bold; text-transform: uppercase;">${
            passenger.type
          }</td>
          <td style="color: #666; text-transform: uppercase;">Issued Date</td>
          <td style="font-weight: bold; text-transform: uppercase;">${formatTicketDate(
            booking.bookingDetails.createdAt,
          )}</td>
        </tr>
        <tr>
          <td style="color: #666; text-transform: uppercase;">Passport Expiry</td>
          <td style="font-weight: bold; text-transform: uppercase;">${
            passenger.passportExpiry || "N/A"
          }</td>
          <td></td>
          <td></td>
        </tr>
      </table>
      
      <table width="100%" border="0" cellspacing="0" cellpadding="8" style="background-color: #cccff2; margin-bottom: 0;">
        <tr>
          <td style="font-weight: bold; font-size: 13px; text-transform: uppercase;">Travel Information</td>
        </tr>
      </table>

      <table width="100%" border="0" cellspacing="0" cellpadding="8" style="border-collapse: collapse; font-size: 12px;">
        <thead style="background-color: #f9f9f9; border-bottom: 1px solid #ddd;">
          <tr>
            <th align="left">FLIGHT</th>
            <th align="left">DEPART / ARRIVE</th>
            <th align="left">DURATION</th>
            <th align="left">AIRPORT</th>
            <th align="left">CLASS</th>
          </tr>
        </thead>
        <tbody>
          ${booking.bookingDetails.flights
            .map(
              (flight: any) => `
            <tr style="background-color: #f3f4ff;">
              <td style="font-weight: bold;">
                ${flight.airline} ${flight.flightNumber}
                <div style="font-weight: normal; font-size: 10px; margin-top: 2px;">CONFIRMED</div>
              </td>
              <td>
                <strong>${formatTicketDate(
                  flight.departureDateTime,
                )}</strong><br/>
                <span style="font-size: 13px;">${formatTicketTime(
                  flight.departureDateTime,
                )}</span>
              </td>
             <td>${calculateDuration(
               flight.duration,
               flight.departureDateTime,
               flight.arrivalDateTime,
             )}</td>
              <td><strong>${flight.originAirport}</strong></td>
              <td>${getTravelClassName(booking.bookingDetails.travelClass)} - ${
                flight.rbd
              }</td>
            </tr>
            <tr style="border-bottom: 1px solid #eee;">
              <td></td>
              <td>
                <strong>${formatTicketDate(
                  flight.arrivalDateTime,
                )}</strong><br/>
                <span style="font-size: 13px;">${formatTicketTime(
                  flight.arrivalDateTime,
                )}</span>
              </td>
              <td></td>
              <td><strong>${flight.destinationAirport}</strong></td>
              <td></td>
            </tr>
          `,
            )
            .join("")}
        </tbody>
      </table>
      
      <div style="margin-top: 25px;">
        <table width="100%" border="0" cellspacing="0" cellpadding="8" style="background-color: #cccff2; margin-bottom: 10px;">
          <tr>
            <td style="font-weight: bold; font-size: 13px; text-transform: uppercase;">Baggage Allowance</td>
          </tr>
        </table>

        ${
          booking.baggageDetails
            ? `
          <table width="100%" border="0" cellspacing="0" cellpadding="5" style="font-size: 12px;">
            <tr>
              ${
                booking.baggageDetails.outbound
                  ? `
              <td width="50%" valign="top">
                <div style="margin-bottom: 5px; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #eee; padding-bottom: 2px;">Outbound Flights</div>
                ${
                  booking.baggageDetails.outbound.adult
                    ? `<div>Adult: <strong>${booking.baggageDetails.outbound.adult.allowance} ${booking.baggageDetails.outbound.adult.unit}</strong></div>`
                    : ""
                }
                ${
                  booking.baggageDetails.outbound.child
                    ? `<div>Child: <strong>${booking.baggageDetails.outbound.child.allowance} ${booking.baggageDetails.outbound.child.unit}</strong></div>`
                    : ""
                }
                ${
                  booking.baggageDetails.outbound.infant
                    ? `<div>Infant: <strong>${booking.baggageDetails.outbound.infant.allowance} ${booking.baggageDetails.outbound.infant.unit}</strong></div>`
                    : ""
                }
              </td>`
                  : ""
              }
              
              ${
                booking.baggageDetails.return
                  ? `
              <td width="50%" valign="top">
                <div style="margin-bottom: 5px; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #eee; padding-bottom: 2px;">Return Flights</div>
                ${
                  booking.baggageDetails.return.adult
                    ? `<div>Adult: <strong>${booking.baggageDetails.return.adult.allowance} ${booking.baggageDetails.return.adult.unit}</strong></div>`
                    : ""
                }
                ${
                  booking.baggageDetails.return.child
                    ? `<div>Child: <strong>${booking.baggageDetails.return.child.allowance} ${booking.baggageDetails.return.child.unit}</strong></div>`
                    : ""
                }
                ${
                  booking.baggageDetails.return.infant
                    ? `<div>Infant: <strong>${booking.baggageDetails.return.infant.allowance} ${booking.baggageDetails.return.infant.unit}</strong></div>`
                    : ""
                }
              </td>`
                  : ""
              }
            </tr>
          </table>
        `
            : `
          <div style="padding: 10px; font-size: 12px;">General Allowance: ${
            booking.bookingDetails.baggaeAllownce || "See Airline Policy"
          }</div>
        `
        }
      </div>
    
      <table width="100%" border="0" cellspacing="0" cellpadding="0" style="margin-top: 40px; border-top: 2px solid #eee; padding-top: 20px;">
        <tr>
          <td width="60%" valign="top" style="font-size: 11px; line-height: 1.5; color: #444;">
            <strong style="text-transform: uppercase; font-size: 12px; color: #000;">Agency Details:</strong><br/>
            ${agencyInfo.name}<br/>
            ${agencyInfo.address}<br/>
            Tel: ${agencyInfo.tel}<br/>
            ${agencyInfo.emails
              .map((email: string) => `<span>${email}</span>`)
              .join("<br/>")}
          </td>
          <td width="40%" align="right" valign="bottom">
             <img src="https://i.ibb.co/wLtmv19/airconnecttravel-logo.png" alt="Air Connect Travel" style="height: 80px;" />
          </td>
        </tr>
      </table>

    </div>
    <div style="page-break-before: always;"></div>`,
    )
    .join("");

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Ticket</title>
    </head>
    <body style="margin:0; padding:0; background-color: #f4f4f4;">
      ${passengersHtml}
    </body>
    </html>`;
};
